"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { Gauge, Puzzle, ScanSearch } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/ai-agents/tailored-apply";
const APP = "/autoapply";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "one-click",
    name: "One Click",
    title: "Click Once on Any Job",
    description:
      "Open a job in Chrome and press Tailored Apply. The agent reads the description, tailors your resume, fills in the form and submits the application.",
    imageSrcUrl: `${IMG}/hero-one-click.png`,
    primaryActionText: "Try Tailored Apply",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "keyword-match",
    name: "Keyword Match",
    title: "Rewritten Around the Job's Keywords",
    description:
      "Your headline, summary, bullets and skills are reordered so the experience the job asks for comes first. You see the ATS score before and after.",
    imageSrcUrl: `${IMG}/hero-keyword-match.png`,
    primaryActionText: "Try Tailored Apply",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
  {
    id: "facts-locked",
    name: "Your Facts Only",
    title: "Tailored, Never Invented",
    description:
      "Employers, job titles, dates, degrees and contact details are copied from your resume unchanged. No skill or number is added unless you wrote it.",
    imageSrcUrl: `${IMG}/hero-facts-locked.png`,
    primaryActionText: "Try Tailored Apply",
    primaryActionLink: APP,
    secondaryActionText: "See How It Works",
    secondaryActionLink: "/how-to-use",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "ats-before-after",
    title: "An ATS Score Before and After",
    subtitle: "PROOF IT HELPED",
    description:
      "Each tailored resume is scored against the job description next to your original. If tailoring doesn't raise the score, your original resume is sent instead and you aren't charged.",
    benefits: [
      "Original and tailored scores side by side",
      "Job keywords found, counted",
      "Original resume used if the score doesn't improve",
      "2 credits, only for a tailored resume",
    ],
    primaryCTA: "Try Tailored Apply",
    primaryLink: APP,
    secondaryCTA: "Score My Resume",
    secondaryLink: "/features/resume-jobfit-scorer",
    imageSrcUrl: `${IMG}/ats-before-after.png`,
    icon: <Gauge className="w-4 h-4" />,
    stats: "Scored against the job",
  },
  {
    id: "keyword-gap",
    title: "See the Keywords You're Missing",
    subtitle: "AN HONEST GAP REPORT",
    description:
      "Keywords from the job that aren't in your resume are listed separately, not slipped in. If you do have that experience, add it to your resume and tailor again.",
    benefits: [
      "Matched and missing keywords per job",
      "Nothing added that you didn't write",
      "Fix gaps in Smart Resume Builder",
      "Know what to prepare for the interview",
    ],
    primaryCTA: "Try Tailored Apply",
    primaryLink: APP,
    secondaryCTA: "Build My Resume",
    secondaryLink: "/features/smart-resume-builder",
    imageSrcUrl: `${IMG}/keyword-gap.png`,
    icon: <ScanSearch className="w-4 h-4" />,
    stats: "Keyword report",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "extension-any-site",
    title: "Works on the Job Pages You Open",
    subtitle: "CHROME EXTENSION",
    description:
      "The MindMyJob extension adds Tailored Apply to job pages in Chrome, from LinkedIn and Naukri to company career sites. It fills the form from your profile and attaches the tailored PDF.",
    benefits: [
      "LinkedIn, Naukri, Indeed and Foundit",
      "Company career sites",
      "Form fields filled from your profile",
      "Screening answers drafted",
    ],
    primaryCTA: "Install the Extension",
    primaryLink: "/how-to-use",
    secondaryCTA: "Compare the Agents",
    secondaryLink: "/ai-agents",
    imageSrcUrl: `${IMG}/extension-any-site.png`,
    icon: <Puzzle className="w-4 h-4" />,
    stats: "Chrome extension",
  },
];

// Replace with real user quotes before publishing.
const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "I use Tailored Apply for the five or six roles a week I really want. My score for a platform role went from 64 to 88, and it didn't touch my titles or dates.",
    name: "Ananya R.",
    role: "Platform Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "The keyword report is the honest part. It told me Istio was missing instead of pretending I knew it, so I knew what to brush up on before the interview.",
    name: "Karthik V.",
    role: "Site Reliability Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "It works on company career pages, not just LinkedIn. One click, and the form is filled and a tailored resume is attached.",
    name: "Sara M.",
    role: "Product Analyst",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=800&fit=crop&q=80",
  },
];

export default function TailoredApplyAgentPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "One Job. One Click.",
            titleHighlight: "A Resume Made for It.",
            subtitle:
              "Open any job in Chrome and click once. The Tailored Apply Agent rewrites your resume around that job's keywords, keeps every fact as you wrote it and submits the application.",
            ctaText: "Try Tailored Apply",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Tailored, and You Can See Why.",
            sectionSubtitle:
              "A score that shows what changed, and a report of what your resume doesn't cover.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "For the Roles That Matter Most",
            subtitle: "From job seekers who tailor every application they care about",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Tailored Apply",
            subtitle: "What it changes, what it never touches and what it costs.",
            faqs,
            ctaTitle: "Ready to send a resume made for the job?",
            ctaText: "Try Tailored Apply",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
